import { useRef, useState } from "react";
import { mutate } from "swr";
import { api } from "@/lib/app";
import { UploadSimple, CheckCircle, Warning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import TidoSyncCard from "@/components/TidoSyncCard";

export default function DataImport({ storeId }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [kind, setKind] = useState("auto");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      const res = await api.post(`/stores/${storeId}/import`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ok: true, ...res.data });
      const label = res.data.kind === "plan" ? "PLÁN" : "REALITA";
      toast.success(`${label}: importovaných ${res.data.imported} záznamov`);
      mutate(`/stores/${storeId}/data-range`);
      mutate(`/stores/${storeId}/plans/range`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Chyba pri importe";
      setResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const clearData = async () => {
    if (!confirm("Naozaj zmazať všetky importované dáta tohto strediska?")) return;
    const res = await api.delete(`/stores/${storeId}/data`);
    toast.success(`Zmazaných ${res.data.deleted} záznamov`);
    setResult(null);
    mutate(`/stores/${storeId}/data-range`);
  };

  return (
    <div className="space-y-5">
      <TidoSyncCard storeId={storeId} />

      <div className="card-flat p-4">
        <p className="label-eyebrow mb-2">Typ súboru</p>
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "auto", label: "Auto", desc: "Detekcia z názvu/obsahu" },
            { id: "realita", label: "📊 Realita", desc: "Hodinové dáta s tržbami → štatistiky" },
            { id: "plan", label: "📋 Plán", desc: "Plán personálu → Simulácia (nepretláča realitu)" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setKind(opt.id)}
              className={`px-3 py-2 rounded text-sm border transition-colors ${
                kind === opt.id
                  ? "border-[var(--brand)] bg-[var(--success-bg)] text-[var(--brand)] font-medium"
                  : "border-[var(--border-default)] bg-white text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
              }`}
              data-testid={`kind-${opt.id}`}
            >
              <div>{opt.label}</div>
              <div className="text-[10px] opacity-75 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
          drag ? "border-[var(--brand)] bg-[var(--success-bg)]" : "border-[var(--border-default)] bg-white hover:bg-[var(--bg-surface-hover)]"
        }`}
        data-testid="upload-dropzone"
      >
        <UploadSimple size={36} weight="duotone" className="text-[var(--brand)] mb-3" />
        <h3 className="font-display text-lg font-semibold mb-1">
          {kind === "plan" ? "Nahrajte plán personálu" : kind === "realita" ? "Nahrajte export hodinových dát" : "Nahrajte súbor (plán alebo realita)"}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] max-w-md">
          Podporované: <strong>.xlsx, .xls, .csv</strong>.{" "}
          {kind === "plan" ? (
            <>Plán môže obsahovať iba <span className="tabular">Dátum, Hodina, Ľudia (- plán)</span> — bez tržieb.</>
          ) : kind === "realita" ? (
            <>Očakávané stĺpce: <span className="tabular">Dátum, Hodina, Ľudia, Tržba</span>.</>
          ) : (
            <>Auto-rozpozná plán vs realitu z názvu/obsahu. Ak treba, vyberte typ vyššie ručne.</>
          )}
        </p>
        <button type="button" className="btn-primary mt-4" disabled={uploading} data-testid="upload-button">
          {uploading ? "Nahrávam..." : "Vybrať súbor"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>

      {result?.ok && (
        <div className="card-flat p-5 flex items-start gap-3" data-testid="import-result-success">
          <CheckCircle size={22} weight="fill" className="text-[var(--success)] mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">
              Import úspešný — <span className={result.kind === "plan" ? "text-[var(--brand)]" : "text-[var(--success)]"}>{result.kind === "plan" ? "📋 PLÁN" : "📊 REALITA"}</span>
              <span className="text-[var(--text-muted)] ml-2 text-xs">({result.kind_source === "užívateľ" ? "ručne zvolené" : "auto-detekcia"})</span>
            </p>
            <p className="text-[var(--text-secondary)] mt-1 tabular">
              Záznamov: {result.imported} · Dní: {result.dates} · {result.first_date} → {result.last_date}
              {result.skipped > 0 && ` · Preskočených: ${result.skipped}`}
            </p>
            {result.kind === "plan" && (
              <p className="text-[var(--text-muted)] mt-1 text-xs">
                Plán sa neukladá medzi hodinové dáta — použije sa len v záložke &bdquo;Simulácia&ldquo; na porovnanie a návrh úprav.
              </p>
            )}
          </div>
        </div>
      )}

      {result && !result.ok && (
        <div className="card-flat p-5 flex items-start gap-3 border-[var(--critical)]" data-testid="import-result-error">
          <Warning size={22} weight="fill" className="text-[var(--critical)] mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Import zlyhal</p>
            <p className="text-[var(--text-secondary)] mt-1">{result.error}</p>
          </div>
        </div>
      )}

      <div className="card-flat p-5">
        <h3 className="font-display text-sm font-semibold mb-2">Ako sa dáta ukladajú</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          Aplikácia automaticky nájde hlavičku (riadok obsahujúci &bdquo;Dátum&ldquo;) a načíta hodinové sloty. Opakovaný import za rovnaké dni dáta prepíše.
        </p>
        <ul className="text-sm space-y-1 list-disc ml-5 text-[var(--text-secondary)]">
          <li><strong>📊 Realita</strong> — ide do hodinových záznamov a používa sa vo všetkých štatistikách (Prehľad, Heatmapa, Hodinový profil, Anomálie, AI správa).</li>
          <li><strong>📋 Plán</strong> — ide do samostatnej kolekcie plánov a používa sa <strong>iba v záložke &bdquo;Simulácia&ldquo;</strong> (Plán vs realita + Plán dopredu). Štatistiky neovplyvňuje.</li>
          <li>Plán a realita môžu byť pre rovnaké dni — nepreťahujú sa.</li>
        </ul>
        <p className="text-sm text-[var(--text-secondary)] mt-3 font-medium">Stĺpce ktoré parser hľadá:</p>
        <ul className="text-sm space-y-1 list-disc ml-5 text-[var(--text-secondary)]">
          <li><strong>Dátum</strong> – ISO alebo dd.mm.yyyy</li>
          <li><strong>Hodina</strong> – rozsah napr. <span className="tabular">7-8</span>, <span className="tabular">17-18</span></li>
          <li><strong>Ľudia</strong> / <strong>Ľudia - plán</strong> – počet zamestnancov v slote</li>
          <li><strong>Tržba</strong> – € (povinné pre realitu, voliteľné pre plán)</li>
          <li><strong>Hodinový výkon</strong> – €/osobohodina (voliteľné, ak je v súbore, prepíše konfiguráciu strediska)</li>
        </ul>
      </div>

      <div className="text-right">
        <button onClick={clearData} className="btn-ghost text-xs inline-flex items-center gap-2 text-[var(--critical)]" data-testid="clear-data-button">
          <Trash size={14} /> Zmazať všetky dáta tohto strediska
        </button>
      </div>
    </div>
  );
}
